import { Signal, computed, inject } from "@angular/core";
import { ActivatedRoute, Params } from "@angular/router";
import { signalStoreFeature, withComputed } from "@ngrx/signals";
import { toSignal } from "@angular/core/rxjs-interop";

export type ParamsConfig = Record<string, (param: string | undefined) => unknown>;

export type ParamsComputed<Config extends ParamsConfig> = {
    [Key in keyof Config]: Config[Key] extends infer TransformFn
        ? TransformFn extends (...args: any[]) => any
            ? Signal<ReturnType<TransformFn>>
            : never
        : never;
};

export function withRouteParams<Config extends ParamsConfig>(config: Config) {
    return signalStoreFeature(
        withComputed(() => {
            const routeParams = injectRouteParams();

            return Object.keys(config).reduce(
                (acc, key) => ({
                    ...acc,
                    [key]: computed(() => {
                        const value = routeParams()[key];

                        return config[key](value);
                    }),
                }),
                {} as ParamsComputed<Config>,
            );
        }),
    );
}

function injectRouteParams(): Signal<Params> {
    const params = inject(ActivatedRoute).params;

    return toSignal(params, {
        initialValue: {} as Record<string, string | undefined>,
    });
}
